<script setup>
import {ref, onMounted } from 'vue'
import ButtonSave from "@/components/common/button/ButtonSave.vue";
import {handleError} from "@/utils/errors.js";
import messageService from '@/services/messages.js'
import {useI18n} from "vue-i18n";
import router from "@/router/index.js";
import {useRoute} from "vue-router";
import {
    change2FA,
    getUserById,
    getUsers,
    reset2FA,
    saveUser,
    sendPasswordAuthorizationKey,
} from "@/services/api/users.js";
import EntityForm from "@/components/common/Form.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import {UserEntity} from "@/entities/UserEntity.js";
import {buildPayloadFromEntity, searchAndSetOptions} from "@/utils/index.js";
import {addUsertoCorp, getCorp, getCorpUsers} from "@/services/api/corporation.js";
import DialogConfirm from "@/components/common/DialogConfirm.vue";
import {
    addUsertoPermissionManagement,
    getPermissionsManagement,
    removeUserFromPermissionManagement
} from "@/services/api/permissionManagement.js";
import { useAuthStore } from "@/stores/authStore";
import { configureSettingsEntityRoutes, getSettingsRoute } from "@/utils/settingsRoutes.js";

const { t } = useI18n();
const route = useRoute();
const loading = ref(false);
const id = route.params.id;
const isMounted = ref(false);
const form = ref(null);
const userEntity = configureSettingsEntityRoutes(new UserEntity(), route);
const entity = ref(userEntity);
const isFetching = ref(false);
const formData = ref(entity.value.getDefaultValues());
const fieldsDisabled = ref(false);
const showDialogConfirm = ref(false);
const showDialogConfirmResetTwoFactor = ref(false);
const showDialogConfirmDisableTwoFactor = ref(false);
const authStore = useAuthStore();
const loadingPasswordEmail = ref(false);

async function onSave() {
    try {
        const { valid } = await form.value.validate();
        if (!valid) return;

        let corp = await getCorp();
        loading.value = true;
        const isUpdate = Boolean(id);

        formData.value.id = id ?? null;

        const payload = buildPayloadFromEntity({
            entity: entity.value,
            data: formData.value
        });

        payload.id = formData.value.id ?? null;

        const permissionsFromForm = Array.isArray(formData.value?.permissions)
            ? formData.value.permissions
            : [];

        const permissionsWithCorp = permissionsFromForm.map(p => ({
            permissionId: p.value,
            corpId: p.corpId
        }));

        const response = await saveUser(payload);
        let permissionsToRemoveIds = [];

        if (isUpdate) {
            const currentPermissionIds = permissionsFromForm.map(p => p.value);

            const oldPermissionIds = Object.keys(formData.value?.permissionsMeta || {});

            permissionsToRemoveIds = oldPermissionIds.filter(
                id => !currentPermissionIds.includes(id)
            );
        }
        if (corp?.id) {

            await addUsertoCorp({
                idCorp: corp.id,
                idUser: response.id
            });
        } else {
            const uniqueCorpIds = [...new Set(permissionsWithCorp.map(p => p.corpId).filter(Boolean))];

            for (const corpId of uniqueCorpIds) {
                await addUsertoCorp({
                    idCorp: corpId,
                    idUser: response.id
                });
            }
        }

        for (const { permissionId } of permissionsWithCorp) {
            await addUsertoPermissionManagement({
                idPermissionManagement: permissionId,
                idUser: response.id
            });
        }

        for (const permissionId of permissionsToRemoveIds) {
            await removeUserFromPermissionManagement({
                idPermissionManagement: permissionId,
                idUser: response.id
            });
        }

        isUpdate
            ? message.success(t("common.messages.success.save"))
            : message.success(t("common.messages.success.userCreate"));

        await router.push(entity.value.pages.list.route?.at(-1));

    } catch (error) {
        if (error.status === 409) {
            if (error.response.data.error === "Email already exists.") {
                const userDuplicate = await getCorpUsers({ email: formData.value.email });

                if (userDuplicate.data.length > 0) {
                    messageService.error(t('common.messages.errors.emailAlreadyExists'));
                    return;
                } else {
                    showDialogConfirm.value = true;
                    return;
                }
            }

            messageService.error(t('common.messages.errors.loginAlreadyExists'));
        } else {
            message.error(await handleError(error, t));
        }

        console.error(error);
    } finally {
        loading.value = false;
    }
}

const confirmUserinCorp = async () => {
    try {
        const corp = await getCorp();
        const user = await getUsers({ email: formData.value.email });

        await addUsertoCorp({
            idCorp: corp.id,
            idUser: user.data[0].id
        });

        message.success(t('common.messages.success.save'));
        showDialogConfirm.value = false;
        router.push(getSettingsRoute('/settings/users', route));
    } catch (error) {
        message.error(await handleError(error, t));
    }
};

async function handleSearch({ handler, query }) {
    const handlers = {
        handleSearchPermission: () =>
            searchAndSetOptions({
                key: "permissions",
                apiFn: getPermissionsManagement,
                query: "%%",
                entity,
                mapFn: item => ({
                    value: item.id,
                    label: `${item.name} (${item.corp_business_name})`,
                    corpId: item.corp_id
                }),
                isFetching
            }),
    };

    if (handlers[handler]) {
        await handlers[handler]();
    } else {
        console.warn(`Handler não encontrado: ${handler}`);
    }
}

async function onResetTwoFactor() {
    showDialogConfirmResetTwoFactor.value = true;
}

async function onDisableTwoFactor() {
    showDialogConfirmDisableTwoFactor.value = true;
}

const confirmResetTwoFactor = async () => {
    try {
        await reset2FA({id});
        message.success(t('common.messages.success.twoFactorResetSuccess'));

    } catch (error) {
        message.error(await handleError(error, t));
    }
    showDialogConfirmResetTwoFactor.value = false;
}

const confirmDisableTwoFactor = async () => {
    try {
        const response = await change2FA({ data: formData.value });
        formData.value.two_factor_required = response?.two_factor_required;

        message.success(t('common.messages.success.twoFactorUsageUpdatedSuccess'));
    } catch (error) {
        console.error(error);
    }

    showDialogConfirmDisableTwoFactor.value = false;
};

const handleSendPasswordResetEmail = async () => {
    try {
        loadingPasswordEmail.value = true;

        if (!formData.value?.login) {
            message.error(t('components.users.userCreate.userLoginNotFound'));
            return;
        }

        await sendPasswordAuthorizationKey({ login: formData.value.login });

        message.success(t('components.users.userCreate.passwordResetEmailSent'));
    } catch (error) {
        message.error(await handleError(error, t));
    } finally {
        loadingPasswordEmail.value = false;
    }
};

onMounted(async () => {
    try {
        isMounted.value = false;

        if (id) {
            const response = await getUserById({ id });
            const data = response.data;

            let permissions = [];
            let permissionsMeta = {};

            if (data.corps) {
                Object.values(data.corps).forEach(corp => {
                    const corpId = corp.id;

                    if (corp.workgroups) {
                        Object.entries(corp.workgroups).forEach(([name, workgroupId]) => {

                            permissions.push({
                                title: name,
                                value: workgroupId
                            });

                            permissionsMeta[workgroupId] = {
                                corpId
                            };
                        });
                    }
                });
            }

            formData.value = {
                ...data,
                permissions
            };

            formData.value.permissionsMeta = permissionsMeta;
            const permissionsField = entity.value.fields.find(f => f.key === "permissions");
            if (permissionsField) {
                permissionsField.options = permissions.map(p => ({
                    label: p.title,
                    value: p.value
                }));
            }
        }
    } catch (error) {
        console.error(error);
    } finally {
        isMounted.value = true;
    }
});
</script>

<template >
    <BasePage :loading-mounted="isMounted" :entity="entity">
        <template v-if="isMounted" #default>
            <v-col cols="12" v-if="id" class="d-flex flex-wrap justify-center justify-md-end ga-2">
                <v-btn
                    @click="handleSendPasswordResetEmail"
                    :loading="loadingPasswordEmail"
                    :disabled="loading"
                    :title="t('components.users.userCreate.sendPasswordResetEmail')"
                    class="bg-primary text-capitalize me-2 ga-2"
                >
                    {{ t('components.users.userCreate.sendPasswordResetEmail') }}
                </v-btn>

                <v-btn
                    @click="onResetTwoFactor"
                    :title="t('components.users.userCreate.resetTwoFactor')"
                    class="bg-primary text-capitalize me-2 ga-2"
                >
                    {{ t('components.users.userCreate.resetTwoFactorShort') }}
                </v-btn>

                <v-btn
                    v-if="authStore.isAdmin"
                    @click="onDisableTwoFactor"
                    :title="t('components.users.userCreate.changeTwoFactorUsage')"
                    class="bg-danger text-capitalize text-white"
                >
                    {{formData?.two_factor_required ? t('components.users.userCreate.disableTwoFactor') : t('components.users.userCreate.enableTwoFactor')}}
                </v-btn>
            </v-col>
            <EntityForm :entity="entity" v-model="formData" :disabled="fieldsDisabled" ref="form" @search="handleSearch" />
            <ButtonSave :loading="loading"  @click="onSave" />
            <DialogConfirm
                v-model="showDialogConfirmResetTwoFactor"
                :title="t('components.users.userCreate.confirmResetTwoFactor')"
                description=""
                @confirm="confirmResetTwoFactor"
            />
            <DialogConfirm
                v-model="showDialogConfirmDisableTwoFactor"
                :title="t('components.users.userCreate.confirmChangeTwoFactorUsage')"
                description=""
                @confirm="confirmDisableTwoFactor"
            />
            <DialogConfirm
                v-model="showDialogConfirm"
                :title="t('components.users.userCreate.userAlreadyRegistered')"
                :description="`${t('components.users.userCreate.linkUserToCorp')}<br/>${t('components.users.userCreate.userDataWillBeKept')}`"
                @confirm="confirmUserinCorp"
            />
        </template>
    </BasePage>
</template>
